import { useCallback, useState } from 'react';

import { ContactProps, ContactType } from '@/models/Contact';
import { PartnerPatchPayload, PartnerProps } from '@/models/Partner';
import { validateContact } from '@/utils/contactUtils';

const pickPartnerFields = (
  source: PartnerProps,
  fieldsToUpdate: Array<keyof PartnerProps>
): Partial<PartnerProps> => {
  const entries = fieldsToUpdate.map((field) => [field, source[field]] as const);
  return Object.fromEntries(entries);
};

type Args = {
  shippingContactType?: ContactType;
  saveContact: (contact: ContactProps) => Promise<ContactProps>;
  patchPartnerRecord: (partnerId: number, patchData: PartnerPatchPayload) => Promise<PartnerProps>;
  setAlert: (a: { message: string; severity: 'warning' | 'success' | 'error' }) => void;
};

export function usePartnerUpdateSubmit({
  shippingContactType,
  saveContact,
  patchPartnerRecord,
  setAlert,
}: Args) {
  const [posting, setPosting] = useState(false);

  const submit = useCallback(
    async ({
      partnerInfoLocal,
      shippingContact,
      fieldsToUpdate,
      onUpdatedPartner,
      onClose,
      onMissingShippingFields,
    }: {
      partnerInfoLocal: PartnerProps;
      shippingContact: ContactProps;
      fieldsToUpdate?: Array<keyof PartnerProps>;
      onUpdatedPartner: (updatedPartner: PartnerProps) => void;
      onClose?: () => void;
      onMissingShippingFields?: () => void;
    }) => {
      const shouldSaveShippingContact = fieldsToUpdate?.includes('shippingContactId') === true;
      let shippingContactId = partnerInfoLocal.shippingContactId;

      if (shouldSaveShippingContact && shippingContactType != null && shippingContact != null) {
        const missingFields = validateContact(shippingContact, shippingContactType);

        if (missingFields.length > 0) {
          setAlert({
            message: "Partner wasn't updated because shipping fields were missing",
            severity: 'warning',
          });
          onMissingShippingFields?.();
          return;
        } else {
          const updatedShippingContact = await saveContact(shippingContact);
          if (updatedShippingContact.id != null) shippingContactId = updatedShippingContact.id;
        }
      }

      setPosting(true);

      const scopedPartnerData =
        fieldsToUpdate != null && fieldsToUpdate.length > 0
          ? pickPartnerFields(partnerInfoLocal, fieldsToUpdate)
          : partnerInfoLocal;

      const partnerToPatch: PartnerPatchPayload = scopedPartnerData;

      if (shouldSaveShippingContact) {
        partnerToPatch.shippingContactId = shippingContactId;
      }

      try {
        if (partnerInfoLocal.id == null) {
          throw new Error('Partner id is required for patch updates');
        }

        const updatedPartner = await patchPartnerRecord(partnerInfoLocal.id, partnerToPatch);
        onUpdatedPartner(updatedPartner);
        onClose?.();
      } catch (error) {
        setAlert({
          message: `Partner update failed. ${error instanceof Error ? error.message : String(error)}`,
          severity: 'error',
        });
      } finally {
        setPosting(false);
      }
    },
    [patchPartnerRecord, saveContact, setAlert, shippingContactType]
  );

  return { posting, submit };
}
